import { useState } from "react";
import { EventTable, IssueStageTable, LockedTable, NaruTable, SaveHints, SessionTable, StageChart, UsagePanel, foldIssueStages, foldUsage, saveHints } from "../components/Ops";
import type { OpsEvent, UsageAxis } from "../components/Ops";
import type { Session } from "../chat/api";
import type { Issue } from "../lib/tasks";
import type { RunnerRow } from "../lib/auth";
import { tx } from "../i18n";

/* ══ 패턴 페이지 › 관측 › 지금의 판(t-in-port-obs-ops) ══
   사용량(값 · 모름 섞임 · 빈) · 잠긴 이슈(있음 · 없음) · 나루 · 세션 이력 · 이벤트. 서버를 안 부른다. */

const NOW = Date.parse("2026-10-31T03:00:00Z");
const at = (min: number) => new Date(NOW - min * 60_000).toISOString();

const sess = (id: string, p: Partial<Session>): Session => ({
  id,
  taskId: "t-in-port-obs-ops",
  taskTitle: "관측 › 지금", // copy-ignore: 견본 자료
  runner: "naru-1",
  track: "run",
  vendor: "claude",
  outcome: "ok",
  model: "claude-sonnet",
  note: null,
  startedAt: at(90),
  endedAt: at(60),
  stopRequestedAt: null,
  inputTokens: 182_000,
  outputTokens: 9_400,
  lineCount: 120,
  ...p,
});
const SESSIONS: Session[] = [
  sess("s1", { endedAt: null, outcome: null, startedAt: at(12), inputTokens: null, outputTokens: null }),
  sess("s2", {}),
  sess("s3", { track: "review", vendor: "codex", model: null, runner: "naru-2", outcome: "auth", startedAt: at(200), endedAt: at(199), inputTokens: null, outputTokens: null }),
  sess("s4", { track: "plan", vendor: "openhands", model: "gpt-oss", startedAt: at(400), endedAt: at(370), inputTokens: 40_200, outputTokens: 3_100 }),
];

const issue = (id: string, title: string, p: Partial<Issue>): Issue =>
  ({ id, title, status: "ready", assignee: "admin", assigneeKind: "ticket", openSession: null, ...p }) as Issue;
const LOCKED: Issue[] = [
  issue("t-in-api-health", "연결 시험 API", { status: "in-progress", assignee: "naru-1", assigneeKind: "held" }), // copy-ignore: 견본 자료
  issue("t-in-web-logs", "로그 뷰어 거르개", { assignee: "o2hp" }), // copy-ignore: 견본 자료
];

const RUNNERS: RunnerRow[] = [
  { name: "naru-1", source: "web", createdBy: "admin", createdAt: at(60 * 24 * 9), connections: 1, presence: { name: "naru-1", runner: "naru-1", tags: [], vendors: ["claude", "codex"], version: "0.14.2", mode: "host", since: at(300) }, lastSeenAt: at(1) },
  { name: "naru-2", source: "env", createdBy: null, createdAt: null, connections: 0, presence: null, lastSeenAt: at(40), downSince: at(38) },
  { name: "naru-3", source: "web", createdBy: "admin", createdAt: at(60), connections: 0, presence: null, lastSeenAt: null },
];

const EVENTS: OpsEvent[] = [
  { taskId: "t-in-port-obs-ops", taskTitle: "관측 › 지금", kind: "status", actor: "naru-1", summary: "", from: "ready", to: "in-progress", at: at(12) }, // copy-ignore: 견본 자료
  { taskId: "t-in-port-obs-ops", taskTitle: "관측 › 지금", kind: "session-open", actor: "naru-1", summary: "run · claude", from: null, to: null, at: at(12) }, // copy-ignore: 견본 자료
  { taskId: "t-in-web-logs", taskTitle: "로그 뷰어 거르개", kind: "link", actor: "o2hp", summary: "pr https://github.com/irolab-co/b3x-in/pull/270", from: null, to: null, at: at(95) }, // copy-ignore: 견본 자료
];

export function OpsStates() {
  const [axis, setAxis] = useState<UsageAxis>("vendor");
  const projectOf = () => ({ project: "in.b3x.app", complexity: "normal" });
  return (
    <>
      <h3>{tx("pat.ops.usage")}</h3>
      <UsagePanel rows={foldUsage(SESSIONS, axis, projectOf)} axis={axis} onAxis={setAxis} capped={false} />
      <h3>{tx("pat.ops.usageEdge")}</h3>
      <div className="pat__grid">
        <UsagePanel rows={foldUsage(SESSIONS.slice(0, 1), "model", projectOf)} axis="model" onAxis={() => undefined} capped />
        <UsagePanel rows={[]} axis="vendor" onAxis={() => undefined} capped={false} />
      </div>
      <h3>{tx("pat.ops.chart")}</h3>
      <StageChart rows={SESSIONS} range="1d" now={NOW} />
      <h3>{tx("pat.ops.stages")}</h3>
      <IssueStageTable rows={foldIssueStages(SESSIONS)} />
      <h3>{tx("pat.ops.save")}</h3>
      <div className="pat__grid">
        <SaveHints hints={saveHints(SESSIONS, foldIssueStages(SESSIONS))} />
        <SaveHints hints={[]} />
      </div>
      <h3>{tx("pat.ops.locked")}</h3>
      <LockedTable rows={LOCKED} busyId="t-in-web-logs" onUnlock={() => undefined} />
      <LockedTable rows={[]} busyId={null} />
      <h3>{tx("pat.ops.naru")}</h3>
      <NaruTable rows={RUNNERS} liveOnly={false} now={NOW} />
      <NaruTable rows={RUNNERS.slice(1)} liveOnly now={NOW} />
      <h3>{tx("pat.ops.sess")}</h3>
      <SessionTable rows={SESSIONS} now={NOW} onLog={() => undefined} />
      <h3>{tx("pat.ops.ev")}</h3>
      <EventTable rows={EVENTS} now={NOW} />
      <EventTable rows={[]} now={NOW} />
    </>
  );
}
